import React, { useState, useEffect } from 'react';

import 'mapbox-gl/dist/mapbox-gl.css';

import { createMapClickHandler, createPointClickHandler } from './MapEventHandlers';

import { 
  shouldShowAutoPath, 
  shouldShowCustomPath, 
  shouldShowUnfinishedPath,
  shouldShowDistanceLabels,

  getMapContainerClassName,
  isMapClickAddPointEnabled,
  isPointSelectionEnabled
} from '../Services/MapServices/MapService';

// Import custom hooks
import { useMapState } from './hooks/useMapState';
import { useUnifiedPathManager } from './hooks/useUnifiedPathManager';

// Import VRP context
import { useVrp } from '../context/VrpContext';


// Import unified styles
import './styles/index.js';

// Import VRP Layer Control
import LayerControl from './LayerControl';

/**
 * VRP Map Component
 * Integrated based on split functional modules
 * Now using VrpContext for state management
 */
export default function MapComponent() {
  // Use VRP context for state management
  const {
    // Mode management
    currentMode,
    setupSubmode,
    
    // Data state
    locations,
    startId,
    setLocations,
    setStartId,
    
    // Path management
    setPath,
    visiblePaths: paths,
    
    // Vehicle path management (for unfinished paths)
    vehiclePaths,
    selectedVehicleIndex,
    pathComplete,
    customVehicleCount,
    
    // Map interaction
    mapPointSelectCallback,
    setMapRef
  } = useVrp();
  
  const [mapError, setMapError] = useState(null);
  
  // Destructure paths object, now it's always an object
  const autoPath = paths.auto || [];
  const customPath = paths.custom || [];
  
  // Use service functions to calculate various path display flags
  const showAutoPath = shouldShowAutoPath(currentMode);
  const showCustomPath = shouldShowCustomPath(currentMode);
  const showUnfinishedPath = shouldShowUnfinishedPath(currentMode);
  const showDistanceLabels = shouldShowDistanceLabels(currentMode);

  // Use custom hooks
  const {
    mapContainer,
    map,
    mapReady,
    sourcesReadyRef,
    isMountedRef
  } = useMapState(showDistanceLabels);
  
  // Simple cleanup for student project

  // Register map reference for jump-to-location functionality
  useEffect(() => {
    if (map.current && mapReady) {
      setMapRef(map.current);
      
      // Core location layer visibility protection
      setTimeout(() => {
        if (map.current && map.current.loaded()) {
          try {
            if (map.current.getLayer('points-circle-layer')) {
              map.current.setLayoutProperty('points-circle-layer', 'visibility', 'visible');
            }
            if (map.current.getLayer('points-symbol-layer')) {
              map.current.setLayoutProperty('points-symbol-layer', 'visibility', 'visible');
            }
          } catch (error) {
            console.warn('[MapComponent] Error ensuring layer visibility:', error);
          }
        }
      }, 100);
    }
  }, [map, mapReady, setMapRef]);
  
  // Global location data exposure removed - debug tools no longer needed

  // Unified path management (replaces previous usePathVisibility and usePathEffects)
  const pathManagerState = useUnifiedPathManager({
    map,
    mapReady,
    sourcesReadyRef,
    isMountedRef,
    
    // Path data
    autoPath,
    customPath,
    vehiclePaths,
    locations,
    startId,
    
    // Display controls
    showAutoPath,
    showCustomPath,
    showUnfinishedPath,
    showDistanceLabels,
    
    // Vehicle related
    selectedVehicleIndex,
    pathComplete,
    customVehicleCount
  });

  // Debug information - show path management state in development environment
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && pathManagerState) {
      console.log('[MapComponent] Path Manager State:', {
        isReady: pathManagerState.isReady,
        hasAutoPath: pathManagerState.hasAutoPath,
        hasVehiclePaths: pathManagerState.hasVehiclePaths,
        currentMode,
        pathComplete,
        selectedVehicleIndex
      });
    }
  }, [pathManagerState, currentMode, pathComplete, selectedVehicleIndex]);

  // Mode change monitoring - simplified after fix
  useEffect(() => {
    // Ensure location layers stay visible during mode changes
    if (map.current && mapReady && locations.length > 0) {
      setTimeout(() => {
        try {
          if (map.current.getLayer('points-circle-layer')) {
            map.current.setLayoutProperty('points-circle-layer', 'visibility', 'visible');
          }
          if (map.current.getLayer('points-symbol-layer')) {
            map.current.setLayoutProperty('points-symbol-layer', 'visibility', 'visible');
          }
        } catch (error) {
          console.warn('[MapComponent] Error during mode change visibility fix:', error);
        }
      }, 50);
    }
  }, [currentMode, map, mapReady, locations]);

  // Create event handlers only if map is ready
  const mapClickHandler = createMapClickHandler(
    map.current, 
    locations, 
    currentMode, 
    setupSubmode, 
    setLocations
  );
  
  const pointClickHandler = createPointClickHandler(
    map.current, 
    locations, 
    startId, 
    currentMode, 
    setStartId, 
    mapPointSelectCallback
  );
  
  // Add event listeners when map is ready - with improved handling
  useEffect(() => {
    if (!map.current || !mapReady) return;// Clean up previous listeners to avoid duplicates
    if (map.current._hasClickListeners) {
      // Remove specific event listeners
      map.current.off('click', map.current._mapClickHandler);
      map.current.off('click', map.current._pointClickHandler);
      map.current._hasClickListeners = false;}
    
    // Store handlers for cleanup
    map.current._mapClickHandler = mapClickHandler;
    map.current._pointClickHandler = pointClickHandler;
    
    // Add map click handler if adding points by clicking is enabled
    if (isMapClickAddPointEnabled(currentMode, setupSubmode)) {
      map.current.on('click', mapClickHandler);
    }
    // Add point click handler if point selection is enabled
    if (isPointSelectionEnabled(currentMode) && mapPointSelectCallback) {
      // Create and attach point click handler
      map.current.on('click', pointClickHandler);
    }
    
    // Mark that event listeners have been added
    map.current._hasClickListeners = true;
    
    // Cleanup function
    return () => {
      if (map.current) {
        // Remove specific event listeners
        map.current.off('click', map.current._mapClickHandler);
        map.current.off('click', map.current._pointClickHandler);
        map.current._hasClickListeners = false;
      }
    };
  }, [map, mapReady, currentMode, setupSubmode, mapClickHandler, pointClickHandler, mapPointSelectCallback]);
  
  // Get container class name with correct parameters
  const containerClassName = getMapContainerClassName(currentMode, setupSubmode);
  
  return (
    <div className={containerClassName} style={{position: 'relative', width: '100%', height: '100%'}}>
      {mapError && (
        <div className="map-error">
          <p>Error loading map: {mapError}</p>
        </div>
      )}

      <div 
        ref={mapContainer} 
        className="vrp-map-container" 
        style={{width: '100%', height: '100%', position: 'absolute'}}
      />

      {/* VRP Layer Control Panel */}
      <LayerControl 
        map={map.current}
        mapReady={mapReady}
        currentMode={currentMode}
        pathComplete={pathComplete}
        selectedVehicleIndex={selectedVehicleIndex}
      />

    </div>
  );
} 